import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Form,
  Select,
  Tag,
  Space,
  Breadcrumb,
  Row,
  Col,
  Statistic,
  Modal,
  Tabs,
  Avatar,
  Typography,
  Descriptions,
  Badge,
  Timeline,
  Divider,
  message,
  DatePicker
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  SyncOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  TeamOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  HistoryOutlined,
  StarFilled,
  ShoppingOutlined,
  CreditCardOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  FileTextOutlined,
  MessageOutlined,
  HeartOutlined,
  HomeOutlined,
  CoffeeOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// 会员数据接口
interface CustomerData {
  key: string;
  memberId: string;
  userId: string;
  name: string;
  nickname: string;
  phone: string;
  email: string;
  avatar: string;
  level: string;
  isAgent: boolean;
  cardType: string;
  cardDiscount: number;
  stayNights: number;
  accountBalance: number;
  availablePoints: number;
  availableCoupons: number;
  cardExpireDate: string;
  source: string;
  registerDate: string;
  totalSpent: number;
  lastOrderDate: string;
  status: string;
  currentButler: {
    id: string;
    name: string;
    bindTime: string;
    bindMethod: string;
  } | null;
}

// 绑定历史记录接口
interface BindingHistoryRecord {
  id: string;
  customerId: string;
  butlerId: string;
  butlerName: string;
  bindTime: string;
  bindMethod: string;
  unbindTime?: string;
  unbindReason?: string;
  status: 'active' | 'inactive';
}

// 会员消费记录接口
interface ConsumptionRecord {
  id: string;
  customerId: string;
  orderNo: string;
  orderTime: string;
  businessType: string;
  amount: number;
  pointsEarned: number;
  paymentMethod: string;
  status: 'pending' | 'confirmed' | 'partly_checkin' | 'checked_in' | 'cancelled' | 'no_show' | 'early_checkout' | 'checked_out' | 'failed';
  location: string;
  remark: string;
  storeName: string; // 门店名称
  orderSource: string; // 订单来源
  memberLevel: string; // 消费时的会员卡级别
  butlerId?: string; // 管家ID
  butlerName?: string; // 管家名称
  items?: {
    name: string;
    quantity: number;
    price: number;
  }[];
}

// 会员互动记录接口
interface InteractionRecord {
  id: string;
  customerId: string;
  interactionType: 'call' | 'visit' | 'message' | 'feedback' | 'complaint' | 'preference';
  time: string;
  staffId: string;
  staffName: string;
  department: string;
  content: string;
  followUpAction?: string;
  status: 'completed' | 'in-progress' | 'pending';
  satisfaction?: number; // 1-5星评价
}

// 管家数据接口
interface ButlerData {
  key: string;
  id: string;
  name: string;
  phone: string;
  department: string;
  position: string;
  status: 'active' | 'inactive';
  customerCount: number;
}

const CustomerManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailVisible, setDetailVisible] = useState<boolean>(false);
  const [bindingModalVisible, setBindingModalVisible] = useState<boolean>(false);
  const [unbindModalVisible, setUnbindModalVisible] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>('1');
  const [bindHistoryActiveKey, setBindHistoryActiveKey] = useState<string>('1');
  const [bindingForm] = Form.useForm();
  const [unbindForm] = Form.useForm();
  const [consumptionActiveKey, setConsumptionActiveKey] = useState<string>('all');
  const [interactionActiveKey, setInteractionActiveKey] = useState<string>('all');
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [orderNoSearch, setOrderNoSearch] = useState<string>('');
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState<string[]>([]);
  const [selectedOrderSources, setSelectedOrderSources] = useState<string[]>([]);
  const [selectedMemberLevels, setSelectedMemberLevels] = useState<string[]>([]);
  const [selectedButlers, setSelectedButlers] = useState<string[]>([]);

  // 模拟会员数据
  const customersData: CustomerData[] = [
    {
      key: '1',
      memberId: 'M10001',
      userId: 'U20001',
      name: '张三',
      nickname: '三哥',
      phone: '138****8001',
      email: 'zhangsan@example.com',
      avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
      level: '黑钻卡',
      isAgent: true,
      cardType: '年卡',
      cardDiscount: 0.8,
      stayNights: 32,
      accountBalance: 3500,
      availablePoints: 12800,
      availableCoupons: 5,
      cardExpireDate: '2024-12-31',
      source: '员工推广',
      registerDate: '2023-03-15',
      totalSpent: 28500,
      lastOrderDate: '2023-06-10',
      status: 'active',
      currentButler: {
        id: 'BT001',
        name: '李管家',
        bindTime: '2023-03-20',
        bindMethod: '前台引导'
      }
    },
    {
      key: '2',
      memberId: 'M10002',
      userId: 'U20002',
      name: '李四',
      nickname: '四爷',
      phone: '138****8002',
      email: 'lisi@example.com',
      avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
      level: '铂金卡',
      isAgent: false,
      cardType: '季卡',
      cardDiscount: 0.85,
      stayNights: 24,
      accountBalance: 1800,
      availablePoints: 8600,
      availableCoupons: 3,
      cardExpireDate: '2024-09-30',
      source: '线上注册',
      registerDate: '2023-02-22',
      totalSpent: 16800,
      lastOrderDate: '2023-05-25',
      status: 'active',
      currentButler: {
        id: 'BT002',
        name: '王管家',
        bindTime: '2023-02-28',
        bindMethod: '预订自动绑定'
      }
    },
    {
      key: '3',
      memberId: 'M10003',
      userId: 'U20003',
      name: '王五',
      nickname: '五哥',
      phone: '138****8003',
      email: 'wangwu@example.com',
      avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
      level: '金卡',
      isAgent: false,
      cardType: '月卡',
      cardDiscount: 0.9,
      stayNights: 16,
      accountBalance: 800,
      availablePoints: 5200,
      availableCoupons: 2,
      cardExpireDate: '2024-07-31',
      source: '扫码获客',
      registerDate: '2023-04-05',
      totalSpent: 12500,
      lastOrderDate: '2023-06-15',
      status: 'active',
      currentButler: null
    },
    {
      key: '4',
      memberId: 'M10004',
      userId: 'U20004',
      name: '赵六',
      nickname: '六子',
      phone: '138****8004',
      email: 'zhaoliu@example.com',
      avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
      level: '银卡',
      isAgent: false,
      cardType: '体验卡',
      cardDiscount: 0.95,
      stayNights: 8,
      accountBalance: 500,
      availablePoints: 2800,
      availableCoupons: 1,
      cardExpireDate: '2024-06-30',
      source: '员工推广',
      registerDate: '2023-05-10',
      totalSpent: 6800,
      lastOrderDate: '2023-06-02',
      status: 'active',
      currentButler: {
        id: 'BT003',
        name: '张管家',
        bindTime: '2023-05-15',
        bindMethod: '前台引导'
      }
    },
    {
      key: '5',
      memberId: 'M10005',
      userId: 'U20005',
      name: '钱七',
      nickname: '七仔',
      phone: '138****8005',
      email: 'qianqi@example.com',
      avatar: 'https://xsgames.co/randomusers/avatar.php?g=female',
      level: '普卡',
      isAgent: false,
      cardType: '普通卡',
      cardDiscount: 0.98,
      stayNights: 3,
      accountBalance: 200,
      availablePoints: 1200,
      availableCoupons: 0,
      cardExpireDate: '2024-05-31',
      source: '线上注册',
      registerDate: '2023-06-01',
      totalSpent: 3200,
      lastOrderDate: '2023-06-05',
      status: 'active',
      currentButler: null
    }
  ];

  // 模拟绑定历史数据
  const bindingHistoryData: BindingHistoryRecord[] = [
    {
      id: 'BH001',
      customerId: '1',
      butlerId: 'BT001',
      butlerName: '李管家',
      bindTime: '2023-03-20',
      bindMethod: '前台引导',
      status: 'active'
    },
    {
      id: 'BH002',
      customerId: '2',
      butlerId: 'BT001',
      butlerName: '李管家',
      bindTime: '2023-02-24',
      bindMethod: '预订自动绑定',
      unbindTime: '2023-02-28',
      unbindReason: '客户要求更换',
      status: 'inactive'
    },
    {
      id: 'BH003',
      customerId: '2',
      butlerId: 'BT002',
      butlerName: '王管家',
      bindTime: '2023-02-28',
      bindMethod: '预订自动绑定',
      status: 'active'
    },
    {
      id: 'BH004',
      customerId: '4',
      butlerId: 'BT003',
      butlerName: '张管家',
      bindTime: '2023-05-15',
      bindMethod: '前台引导',
      status: 'active'
    }
  ];

  // 模拟会员消费记录数据
  const consumptionRecordsData: ConsumptionRecord[] = [
    {
      id: 'CR001',
      customerId: '1',
      orderNo: 'ORD20230610001',
      orderTime: '2023-06-10 19:30:22',
      businessType: '住宿',
      amount: 3800,
      pointsEarned: 3800,
      paymentMethod: '会员卡支付',
      status: 'checked_out',
      location: '秋果酒店-中央大道店',
      storeName: '中央大道店',
      orderSource: '前台',
      memberLevel: '黑钻卡',
      butlerId: 'BT001',
      butlerName: '李管家',
      remark: '豪华套房2晚，含双早',
      items: [
        { name: '豪华套房', quantity: 2, price: 1680 },
        { name: '双人早餐', quantity: 4, price: 110 }
      ]
    },
    {
      id: 'CR002',
      customerId: '1',
      orderNo: 'ORD20230528002',
      orderTime: '2023-05-28 12:45:36',
      businessType: '餐饮',
      amount: 1260,
      pointsEarned: 1260,
      paymentMethod: '微信支付',
      status: 'confirmed',
      location: '秋果酒店-中央大道店-锦悦中餐厅',
      storeName: '中央大道店',
      orderSource: '小程序',
      memberLevel: '黑钻卡',
      butlerId: 'BT001',
      butlerName: '李管家',
      remark: '商务宴请',
      items: [
        { name: '秋果特色套餐A', quantity: 4, price: 268 },
        { name: '珍品花雕酒', quantity: 1, price: 188 }
      ]
    },
    {
      id: 'CR003',
      customerId: '1',
      orderNo: 'ORD20230515003',
      orderTime: '2023-05-15 16:20:15',
      businessType: 'SPA服务',
      amount: 1580,
      pointsEarned: 1580,
      paymentMethod: '会员卡支付',
      status: 'checked_out',
      location: '秋果酒店-中央大道店-泉心SPA',
      storeName: '中央大道店',
      orderSource: '协议小程序',
      memberLevel: '黑钻卡',
      butlerId: 'BT002',
      butlerName: '王管家',
      remark: '贵宾套餐',
      items: [
        { name: '东方御境SPA套餐', quantity: 1, price: 1580 }
      ]
    },
    {
      id: 'CR004',
      customerId: '1',
      orderNo: 'ORD20230502004',
      orderTime: '2023-05-02 10:15:08',
      businessType: '会员充值',
      amount: 5000,
      pointsEarned: 500,
      paymentMethod: '支付宝支付',
      status: 'confirmed',
      location: '秋果酒店-线上',
      storeName: '线上平台',
      orderSource: '官网',
      memberLevel: '铂金卡',
      remark: '会员卡充值',
      items: [
        { name: '会员卡充值5000元', quantity: 1, price: 5000 }
      ]
    },
    {
      id: 'CR005',
      customerId: '1',
      orderNo: 'ORD20230420005',
      orderTime: '2023-04-20 20:30:45',
      businessType: '商品零售',
      amount: 680,
      pointsEarned: 680,
      paymentMethod: '会员卡支付',
      status: 'checked_out',
      location: '秋果酒店-中央大道店-臻品廊',
      storeName: '中央大道店',
      orderSource: '前台',
      memberLevel: '铂金卡',
      butlerId: 'BT003',
      butlerName: '张管家',
      remark: '购买伴手礼',
      items: [
        { name: '秋果定制茶叶礼盒', quantity: 2, price: 298 },
        { name: '手工香薰蜡烛', quantity: 1, price: 84 }
      ]
    }
  ];

  // 模拟会员互动记录数据
  const interactionRecordsData: InteractionRecord[] = [
    {
      id: 'IR001',
      customerId: '1',
      interactionType: 'call',
      time: '2023-06-05 09:30:15',
      staffId: 'BT001',
      staffName: '李管家',
      department: '客户服务部',
      content: '电话回访，了解客户上次入住体验，客户对服务表示满意，特别称赞了早餐品质。',
      followUpAction: '已记录客户偏好，下次入住安排相同房型和楼层',
      status: 'completed',
      satisfaction: 5
    },
    {
      id: 'IR002',
      customerId: '1',
      interactionType: 'message',
      time: '2023-05-25 15:45:22',
      staffId: 'BT001',
      staffName: '李管家',
      department: '客户服务部',
      content: '短信通知客户6月促销活动，客户回复表示有意向参与。',
      followUpAction: '已将客户添加至VIP预约名单',
      status: 'completed',
      satisfaction: 4
    },
    {
      id: 'IR003',
      customerId: '1',
      interactionType: 'complaint',
      time: '2023-05-15 18:20:35',
      staffId: 'BT001',
      staffName: '李管家',
      department: '客户服务部',
      content: '客户反映SPA服务时预约时间出现混淆，导致等待时间过长。',
      followUpAction: '已向客户致歉并赠送价值500元的SPA抵用券，已优化预约流程。',
      status: 'completed',
      satisfaction: 3
    },
    {
      id: 'IR004',
      customerId: '1',
      interactionType: 'visit',
      time: '2023-05-10 14:30:40',
      staffId: 'BT001',
      staffName: '李管家',
      department: '客户服务部',
      content: '客户入住期间拜访，确认各项服务安排是否满意。客户要求增加枕头和加湿器。',
      followUpAction: '已立即安排送达客户房间，并更新客户喜好档案',
      status: 'completed',
      satisfaction: 5
    },
    {
      id: 'IR005',
      customerId: '1',
      interactionType: 'preference',
      time: '2023-04-28 11:15:18',
      staffId: 'BT001',
      staffName: '李管家',
      department: '客户服务部',
      content: '记录客户生日信息及特殊饮食偏好：不吃海鲜，喜欢中式点心。',
      followUpAction: '已更新客户档案，并安排生日惊喜准备',
      status: 'in-progress',
      satisfaction: 5
    }
  ];

  // 模拟管家数据
  const butlerData: ButlerData[] = [
    {
      key: 'BT001',
      id: 'BT001',
      name: '李管家',
      phone: '13900001111',
      department: '客户服务部',
      position: '高级管家',
      status: 'active',
      customerCount: 8
    },
    {
      key: 'BT002',
      id: 'BT002',
      name: '王管家',
      phone: '13900002222',
      department: '客户服务部',
      position: '资深管家',
      status: 'active',
      customerCount: 6
    },
    {
      key: 'BT003',
      id: 'BT003',
      name: '张管家',
      phone: '13900003333',
      department: '客户服务部',
      position: '初级管家',
      status: 'active',
      customerCount: 4
    }
  ];

  // 获取客户等级对应的标签颜色
  const getLevelColor = (level: string) => {
    switch (level) {
      case '黑钻卡': return 'black';
      case '铂金卡': return 'purple';
      case '金卡': return 'gold';
      case '银卡': return 'silver';
      default: return 'blue';
    }
  };

  // 获取客户列表的列定义
  const columns: ColumnsType<CustomerData> = [
    {
      title: '会员信息',
      key: 'memberInfo',
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.avatar} size="small" style={{ marginRight: 8 }} />
          <div>
            <div><b>{record.name}</b> {record.isAgent && <Tag color="gold" style={{ marginLeft: 4 }}>代理</Tag>}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.memberId}</div>
            <div style={{ fontSize: 12, color: '#999' }}>
              <Tag color={record.status === 'active' ? 'green' : 'red'}>
                {record.status === 'active' ? '正常' : '冻结'}
              </Tag>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 140,
      render: (_, record) => (
        <div>
          <div><PhoneOutlined style={{ marginRight: 8 }} />{record.phone}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            <MailOutlined style={{ marginRight: 8 }} />{record.email}
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            昵称: {record.nickname}
          </div>
        </div>
      ),
    },
    {
      title: '会员等级',
      key: 'memberLevel',
      width: 120,
      render: (_, record) => (
        <div>
          <Tag color={getLevelColor(record.level)}>{record.level}</Tag>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            {record.cardType} | {(record.cardDiscount * 10).toFixed(1)}折
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>
            到期: {record.cardExpireDate}
          </div>
        </div>
      ),
      filters: [
        { text: '黑钻卡', value: '黑钻卡' },
        { text: '铂金卡', value: '铂金卡' },
        { text: '金卡', value: '金卡' },
        { text: '银卡', value: '银卡' },
        { text: '普卡', value: '普卡' },
      ],
      onFilter: (value, record) => record.level === value,
    },
    {
      title: '账户资产',
      key: 'assets',
      width: 140,
      render: (_, record) => (
        <div>
          <div>余额: ¥{record.accountBalance.toLocaleString()}</div>
          <div>积分: {record.availablePoints.toLocaleString()}</div>
          <div>优惠券: {record.availableCoupons}张</div>
        </div>
      ),
    },
    {
      title: '消费情况',
      key: 'consumption',
      width: 140,
      render: (_, record) => (
        <div>
          <div>总消费: ¥{record.totalSpent.toLocaleString()}</div>
          <div>累计间夜: {record.stayNights}晚</div>
          <div>最近消费: {record.lastOrderDate}</div>
        </div>
      ),
      sorter: (a, b) => a.totalSpent - b.totalSpent,
    },
    {
      title: '专属管家',
      key: 'butler',
      width: 120,
      render: (_, record) => (
        <span>
          {record.currentButler ? (
            <Tag color="green">{record.currentButler.name}</Tag>
          ) : (
            <Tag color="red">未绑定</Tag>
          )}
        </span>
      ),
      filters: [
        { text: '已绑定', value: 'bound' },
        { text: '未绑定', value: 'unbound' },
      ],
      onFilter: (value, record) => {
        if (value === 'bound') return record.currentButler !== null;
        if (value === 'unbound') return record.currentButler === null;
        return true;
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.currentButler ? (
            <Button type="link" size="small" danger onClick={() => handleUnbind(record)}>
              解绑
            </Button>
          ) : (
            <Button type="link" size="small" onClick={() => handleBind(record)}>
              绑定管家
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 查看客户详情
  const handleViewDetail = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setDetailVisible(true);
    setActiveTabKey('1');
  };

  // 绑定管家
  const handleBind = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setBindingModalVisible(true);
    bindingForm.resetFields();
  };

  // 解绑管家
  const handleUnbind = (customer: CustomerData) => {
    if (!customer.currentButler) return;
    setSelectedCustomer(customer);
    setUnbindModalVisible(true);
    unbindForm.resetFields();
  };

  // 提交绑定
  const handleBindSubmit = () => {
    bindingForm.validateFields().then(values => {
      console.log('绑定管家:', values);
      message.success(`已成功为 ${selectedCustomer?.name} 绑定 ${values.butlerName} 管家`);
      setBindingModalVisible(false);
      // 在实际应用中，这里应该调用API更新数据
    }).catch(errorInfo => {
      console.log('表单验证失败:', errorInfo);
    });
  };

  // 提交解绑
  const handleUnbindSubmit = () => {
    unbindForm.validateFields().then(values => {
      console.log('解绑管家:', values);
      message.success(`已成功为 ${selectedCustomer?.name} 解绑管家`);
      setUnbindModalVisible(false);
      // 在实际应用中，这里应该调用API更新数据
    }).catch(errorInfo => {
      console.log('表单验证失败:', errorInfo);
    });
  };

  // 根据客户ID获取绑定历史记录
  const getBindingHistory = (customerId: string) => {
    return bindingHistoryData.filter(record => record.customerId === customerId);
  };

  // 表格行选择
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // 获取会员等级统计
  const getLevelStatistics = () => {
    const result: Record<string, number> = {};
    customersData.forEach(customer => {
      if (result[customer.level]) {
        result[customer.level]++;
      } else {
        result[customer.level] = 1;
      }
    });
    return result;
  };

  // 获取绑定状态统计
  const getBindingStatistics = () => {
    const bound = customersData.filter(c => c.currentButler !== null).length;
    const unbound = customersData.length - bound;
    return { bound, unbound };
  };

  // 根据客户ID获取消费记录
  const getConsumptionRecords = (customerId: string) => {
    return consumptionRecordsData.filter(record => record.customerId === customerId);
  };

  // 根据客户ID获取互动记录
  const getInteractionRecords = (customerId: string) => {
    return interactionRecordsData.filter(record => record.customerId === customerId);
  };

  // 获取消费类型对应的图标
  const getBusinessTypeIcon = (type: string) => {
    switch (type) {
      case '住宿': return <HomeOutlined />;
      case '餐饮': return <CoffeeOutlined />;
      case 'SPA服务': return <HeartOutlined />;
      case '会员充值': return <CreditCardOutlined />;
      case '商品零售': return <ShoppingOutlined />;
      case '会议服务': return <TeamOutlined />;
      default: return <ShoppingOutlined />;
    }
  };

  // 获取消费状态对应的标签
  const getOrderStatusTag = (status: string) => {
    switch (status) {
      case 'pending': return <Tag icon={<ClockCircleOutlined />} color="warning">待确认</Tag>;
      case 'confirmed': return <Tag icon={<CheckCircleOutlined />} color="processing">已确认</Tag>;
      case 'partly_checkin': return <Tag icon={<CheckCircleOutlined />} color="processing">部分入住</Tag>;
      case 'checked_in': return <Tag icon={<CheckCircleOutlined />} color="success">已入住</Tag>;
      case 'cancelled': return <Tag icon={<CloseCircleOutlined />} color="error">已取消</Tag>;
      case 'no_show': return <Tag icon={<CloseCircleOutlined />} color="error">noshow</Tag>;
      case 'early_checkout': return <Tag icon={<CheckCircleOutlined />} color="warning">提前离店</Tag>;
      case 'checked_out': return <Tag icon={<CheckCircleOutlined />} color="success">已退房</Tag>;
      case 'failed': return <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>;
      default: return <Tag color="default">{status}</Tag>;
    }
  };

  // 获取互动类型对应的图标和文字
  const getInteractionTypeDisplay = (type: string) => {
    switch (type) {
      case 'call': return { icon: <PhoneOutlined />, text: '电话沟通', color: 'blue' };
      case 'visit': return { icon: <EnvironmentOutlined />, text: '上门拜访', color: 'green' };
      case 'message': return { icon: <MessageOutlined />, text: '短信/消息', color: 'purple' };
      case 'feedback': return { icon: <CommentOutlined />, text: '意见反馈', color: 'cyan' };
      case 'complaint': return { icon: <FileTextOutlined />, text: '投诉处理', color: 'red' };
      case 'preference': return { icon: <HeartOutlined />, text: '偏好记录', color: 'pink' };
      default: return { icon: <CommentOutlined />, text: type, color: 'default' };
    }
  };

  // 生成满意度星级显示
  const renderSatisfactionStars = (satisfaction?: number) => {
    if (!satisfaction) return <Text type="secondary">未评价</Text>;
    
    return (
      <span>
        {[...Array(5)].map((_, i) => (
          <StarFilled key={i} style={{ color: i < satisfaction ? '#faad14' : '#f0f0f0', marginRight: 2 }} />
        ))}
      </span>
    );
  };

  // 业务类型选项
  const businessTypeOptions = [
    { value: '住宿', label: '住宿' },
    { value: '餐饮', label: '餐饮' },
    { value: 'SPA服务', label: 'SPA服务' },
    { value: '会员充值', label: '会员充值' },
    { value: '商品零售', label: '商品零售' },
    { value: '会议服务', label: '会议服务' },
    { value: '其他服务', label: '其他服务' },
  ];

  // 订单状态选项
  const orderStatusOptions = [
    { value: 'pending', label: '待确认' },
    { value: 'confirmed', label: '已确认' },
    { value: 'partly_checkin', label: '部分入住' },
    { value: 'checked_in', label: '已入住' },
    { value: 'cancelled', label: '已取消' },
    { value: 'no_show', label: 'noshow' },
    { value: 'early_checkout', label: '提前离店' },
    { value: 'checked_out', label: '已退房' },
    { value: 'failed', label: '失败' },
  ];

  // 订单来源选项
  const orderSourceOptions = [
    { value: '前台', label: '前台' },
    { value: '协议小程序', label: '协议小程序' },
    { value: '小程序', label: '小程序' },
    { value: '官网', label: '官网' },
    { value: 'ota直连', label: 'ota直连' },
    { value: '企业直连', label: '企业直连' },
    { value: '畅联', label: '畅联' },
  ];

  // 会员等级选项
  const memberLevelOptions = [
    { value: '黑钻卡', label: '黑钻卡' },
    { value: '铂金卡', label: '铂金卡' },
    { value: '金卡', label: '金卡' },
    { value: '银卡', label: '银卡' },
    { value: '普卡', label: '普卡' },
  ];

  // 获取所有可用的管家选项
  const getButlerOptions = () => {
    // 获取不重复的管家列表
    const butlers = Array.from(new Set(
      consumptionRecordsData
        .filter(record => record.butlerName)
        .map(record => record.butlerName)
    ));
    
    return butlers.map(name => ({
      value: name,
      label: name
    }));
  };

  // 根据选择的业务类型和日期范围过滤消费记录
  const getFilteredConsumptionRecords = (customerId: string) => {
    return consumptionRecordsData
      .filter(record => record.customerId === customerId)
      .filter(record => {
        // 过滤业务类型
        if (selectedBusinessTypes.length > 0 && !selectedBusinessTypes.includes(record.businessType)) {
          return false;
        }
        
        // 过滤订单号
        if (orderNoSearch && !record.orderNo.toLowerCase().includes(orderNoSearch.toLowerCase())) {
          return false;
        }
        
        // 过滤订单状态
        if (selectedOrderStatuses.length > 0 && !selectedOrderStatuses.includes(record.status)) {
          return false;
        }
        
        // 过滤订单来源
        if (selectedOrderSources.length > 0 && !selectedOrderSources.includes(record.orderSource)) {
          return false;
        }
        
        // 过滤会员等级
        if (selectedMemberLevels.length > 0 && !selectedMemberLevels.includes(record.memberLevel)) {
          return false;
        }
        
        // 过滤管家
        if (selectedButlers.length > 0) {
          if (!record.butlerName || !selectedButlers.includes(record.butlerName)) {
            return false;
          }
        }
        
        // 过滤日期范围
        if (dateRange && dateRange[0] && dateRange[1]) {
          const recordDate = new Date(record.orderTime);
          const startDate = new Date(dateRange[0]);
          const endDate = new Date(dateRange[1]);
          endDate.setHours(23, 59, 59, 999); // 设置为当天的结束时间
          
          if (recordDate < startDate || recordDate > endDate) {
            return false;
          }
        }
        
        return true;
      });
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    setDateRange(dateStrings[0] && dateStrings[1] ? dateStrings : null);
  };

  // 处理业务类型选择变化
  const handleBusinessTypeChange = (value: string[]) => {
    setSelectedBusinessTypes(value);
  };

  // 处理订单状态选择变化
  const handleOrderStatusChange = (value: string[]) => {
    setSelectedOrderStatuses(value);
  };

  // 处理订单来源选择变化
  const handleOrderSourceChange = (value: string[]) => {
    setSelectedOrderSources(value);
  };

  // 处理会员等级选择变化
  const handleMemberLevelChange = (value: string[]) => {
    setSelectedMemberLevels(value);
  };

  // 处理订单号搜索变化
  const handleOrderNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderNoSearch(e.target.value);
  };

  // 处理管家选择变化
  const handleButlerChange = (value: string[]) => {
    setSelectedButlers(value);
  };

  // 重置筛选条件
  const resetFilters = () => {
    setSelectedBusinessTypes([]);
    setDateRange(null);
    setOrderNoSearch('');
    setSelectedOrderStatuses([]);
    setSelectedOrderSources([]);
    setSelectedMemberLevels([]);
    setSelectedButlers([]);
  };

  return (
    <div className="customer-management">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '会员管理' },
        ]}
      />

      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Item label="会员ID/姓名/手机号">
            <Input placeholder="请输入" allowClear />
          </Form.Item>
          <Form.Item label="会员等级">
            <Select style={{ width: 120 }} placeholder="请选择" allowClear>
              <Option value="黑钻卡">黑钻卡</Option>
              <Option value="铂金卡">铂金卡</Option>
              <Option value="金卡">金卡</Option>
              <Option value="银卡">银卡</Option>
              <Option value="普卡">普卡</Option>
            </Select>
          </Form.Item>
          <Form.Item label="管家">
            <Select style={{ width: 120 }} placeholder="请选择" allowClear>
              <Option value="bound">已绑定</Option>
              <Option value="unbound">未绑定</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />}>搜索</Button>
            <Button style={{ marginLeft: 8 }} icon={<SyncOutlined />}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 数据统计区域 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="会员总数" 
              value={customersData.length} 
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已绑定管家" 
              value={getBindingStatistics().bound} 
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="消费总额" 
              value={customersData.reduce((sum, customer) => sum + customer.totalSpent, 0)} 
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="高级会员占比" 
              value={customersData.filter(c => c.level === '黑钻卡' || c.level === '铂金卡' || c.level === '金卡').length / customersData.length * 100} 
              suffix="%"
              precision={1}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 客户列表 */}
      <Card 
        title="会员列表"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />}>新增会员</Button>
            <Button icon={<ExportOutlined />}>导出</Button>
          </Space>
        }
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={customersData}
          rowKey="key"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 客户详情弹窗 */}
      <Modal
        title={`会员详情 - ${selectedCustomer?.name}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={null}
      >
        {selectedCustomer && (
          <>
            <Tabs activeKey={activeTabKey} onChange={setActiveTabKey}>
              <TabPane tab="基本信息" key="1">
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <Card size="small" title="会员基础资料">
                      <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="会员ID">{selectedCustomer.memberId}</Descriptions.Item>
                        <Descriptions.Item label="用户ID">{selectedCustomer.userId}</Descriptions.Item>
                        <Descriptions.Item label="是否代理">{selectedCustomer.isAgent ? '是' : '否'}</Descriptions.Item>
                        <Descriptions.Item label="注册日期">{selectedCustomer.registerDate}</Descriptions.Item>
                        <Descriptions.Item label="会员来源">{selectedCustomer.source}</Descriptions.Item>
                        <Descriptions.Item label="累计间夜">{selectedCustomer.stayNights}晚</Descriptions.Item>
                        <Descriptions.Item label="消费总额">¥{selectedCustomer.totalSpent.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="最近消费">{selectedCustomer.lastOrderDate}</Descriptions.Item>
                        <Descriptions.Item label="会员状态">
                          <Badge status="success" text="活跃" />
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="会员卡信息">
                      <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="会员等级">{selectedCustomer.level}</Descriptions.Item>
                        <Descriptions.Item label="会员卡">{selectedCustomer.cardType}</Descriptions.Item>
                        <Descriptions.Item label="折扣权益">{(selectedCustomer.cardDiscount * 10).toFixed(1)}折</Descriptions.Item>
                        <Descriptions.Item label="卡到期时间">{selectedCustomer.cardExpireDate}</Descriptions.Item>
                        <Descriptions.Item label="账户余额">¥{selectedCustomer.accountBalance.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="可用积分">{selectedCustomer.availablePoints.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="可用优惠券">{selectedCustomer.availableCoupons}张</Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  {/* 管家绑定信息区块 */}
                  <Col span={12}>
                    <Card 
                      title="专属管家信息" 
                      extra={
                        selectedCustomer.currentButler ? (
                          <Button type="link" danger size="small" onClick={() => handleUnbind(selectedCustomer)}>解除绑定</Button>
                        ) : (
                          <Button type="link" size="small" onClick={() => handleBind(selectedCustomer)}>绑定管家</Button>
                        )
                      }
                    >
                      {selectedCustomer.currentButler ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                            <Avatar size={48} icon={<TeamOutlined />} style={{ backgroundColor: '#722ed1' }} />
                            <div style={{ marginLeft: 16 }}>
                              <Title level={5} style={{ margin: 0 }}>{selectedCustomer.currentButler.name}</Title>
                              <Text type="secondary">专属管家</Text>
                            </div>
                          </div>
                          <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="绑定时间">{selectedCustomer.currentButler.bindTime}</Descriptions.Item>
                            <Descriptions.Item label="绑定方式">{selectedCustomer.currentButler.bindMethod}</Descriptions.Item>
                            <Descriptions.Item label="绑定状态">
                              <Badge status="success" text="生效中" />
                            </Descriptions.Item>
                          </Descriptions>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                          <Title level={5}>未绑定管家</Title>
                          <Paragraph type="secondary">客户暂未绑定专属管家，可以点击右上角按钮进行绑定</Paragraph>
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              {/* 管家绑定历史记录标签页 */}
              <TabPane tab="会员绑定历史" key="2">
                <Card
                  tabList={[
                    {
                      key: '1',
                      tab: '全部记录',
                    },
                    {
                      key: '2',
                      tab: '生效中',
                    },
                    {
                      key: '3',
                      tab: '已解绑',
                    },
                  ]}
                  activeTabKey={bindHistoryActiveKey}
                  onTabChange={setBindHistoryActiveKey}
                >
                  {getBindingHistory(selectedCustomer.key).length > 0 ? (
                    <Timeline mode="left">
                      {getBindingHistory(selectedCustomer.key)
                        .filter(record => {
                          if (bindHistoryActiveKey === '1') return true;
                          if (bindHistoryActiveKey === '2') return record.status === 'active';
                          if (bindHistoryActiveKey === '3') return record.status === 'inactive';
                          return true;
                        })
                        .sort((a, b) => new Date(b.bindTime).getTime() - new Date(a.bindTime).getTime())
                        .map(record => (
                          <Timeline.Item 
                            key={record.id}
                            color={record.status === 'active' ? 'green' : 'red'}
                            label={record.bindTime}
                          >
                            <Card 
                              size="small" 
                              title={
                                <span>
                                  绑定管家: <Tag color="blue">{record.butlerName}</Tag>
                                  {record.status === 'active' ? 
                                    <Badge status="success" text="生效中" style={{ marginLeft: 8 }} /> : 
                                    <Badge status="error" text="已解绑" style={{ marginLeft: 8 }} />
                                  }
                                </span>
                              }
                              style={{ marginBottom: 16 }}
                            >
                              <Descriptions column={1} size="small">
                                <Descriptions.Item label="绑定方式">{record.bindMethod}</Descriptions.Item>
                                {record.status === 'inactive' && (
                                  <>
                                    <Descriptions.Item label="解绑时间">{record.unbindTime}</Descriptions.Item>
                                    <Descriptions.Item label="解绑原因">{record.unbindReason}</Descriptions.Item>
                                  </>
                                )}
                              </Descriptions>
                            </Card>
                          </Timeline.Item>
                        ))
                      }
                    </Timeline>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Title level={5}>暂无绑定历史记录</Title>
                      <Paragraph type="secondary">该客户尚未绑定过管家服务</Paragraph>
                    </div>
                  )}
                </Card>
              </TabPane>

              <TabPane tab="消费记录" key="3">
                <Card
                  title="消费记录"
                  extra={
                    <Space wrap>
                      <Input
                        placeholder="订单号查询"
                        value={orderNoSearch}
                        onChange={handleOrderNoChange}
                        style={{ width: 160 }}
                        allowClear
                      />
                      <Select 
                        mode="multiple"
                        allowClear
                        placeholder="消费类型"
                        value={selectedBusinessTypes}
                        onChange={handleBusinessTypeChange}
                        style={{ width: 160 }}
                        options={businessTypeOptions}
                        maxTagCount={1}
                      />
                      <Select 
                        mode="multiple"
                        allowClear
                        placeholder="订单状态"
                        value={selectedOrderStatuses}
                        onChange={handleOrderStatusChange}
                        style={{ width: 160 }}
                        options={orderStatusOptions}
                        maxTagCount={1}
                      />
                      <Select 
                        mode="multiple"
                        allowClear
                        placeholder="订单来源"
                        value={selectedOrderSources}
                        onChange={handleOrderSourceChange}
                        style={{ width: 160 }}
                        options={orderSourceOptions}
                        maxTagCount={1}
                      />
                      <Select 
                        mode="multiple"
                        allowClear
                        placeholder="会员等级"
                        value={selectedMemberLevels}
                        onChange={handleMemberLevelChange}
                        style={{ width: 160 }}
                        options={memberLevelOptions}
                        maxTagCount={1}
                      />
                      <Select 
                        mode="multiple"
                        allowClear
                        placeholder="服务管家"
                        value={selectedButlers}
                        onChange={handleButlerChange}
                        style={{ width: 160 }}
                        options={getButlerOptions()}
                        maxTagCount={1}
                      />
                      <DatePicker.RangePicker 
                        onChange={handleDateRangeChange}
                        value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                        style={{ width: 240 }}
                      />
                      <Button size="small" icon={<SyncOutlined />} onClick={resetFilters}>重置</Button>
                      <Button size="small" icon={<ExportOutlined />}>导出</Button>
                    </Space>
                  }
                >
                  {getConsumptionRecords(selectedCustomer.key).length > 0 ? (
                    <div>
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                          <Statistic 
                            title="消费总额" 
                            value={getFilteredConsumptionRecords(selectedCustomer.key).reduce((sum, record) => sum + record.amount, 0)} 
                            prefix="¥"
                            precision={2}
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic 
                            title="消费次数" 
                            value={getFilteredConsumptionRecords(selectedCustomer.key).length} 
                            suffix="次"
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic 
                            title="累计积分" 
                            value={getFilteredConsumptionRecords(selectedCustomer.key).reduce((sum, record) => sum + record.pointsEarned, 0)} 
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic 
                            title="平均单价" 
                            value={
                              getFilteredConsumptionRecords(selectedCustomer.key).length > 0 
                                ? getFilteredConsumptionRecords(selectedCustomer.key).reduce((sum, record) => sum + record.amount, 0) / 
                                  getFilteredConsumptionRecords(selectedCustomer.key).length 
                                : 0
                            } 
                            prefix="¥"
                            precision={2}
                          />
                        </Col>
                      </Row>
                      
                      <Table
                        dataSource={getFilteredConsumptionRecords(selectedCustomer.key)
                          .sort((a, b) => new Date(b.orderTime).getTime() - new Date(a.orderTime).getTime())
                        }
                        rowKey="id"
                        size="small"
                        pagination={{ pageSize: 5 }}
                        expandable={{
                          expandedRowRender: record => (
                            <div style={{ padding: '0 20px' }}>
                              <Descriptions title="订单详情" size="small" column={2} bordered>
                                <Descriptions.Item label="订单备注">{record.remark}</Descriptions.Item>
                                <Descriptions.Item label="交易地点">{record.location}</Descriptions.Item>
                              </Descriptions>
                              <Table
                                dataSource={record.items}
                                columns={[
                                  { title: '项目名称', dataIndex: 'name', key: 'name' },
                                  { title: '数量', dataIndex: 'quantity', key: 'quantity' },
                                  { title: '单价', dataIndex: 'price', key: 'price', render: price => `¥${price.toFixed(2)}` },
                                  { 
                                    title: '小计', 
                                    key: 'subtotal', 
                                    render: (_, item) => `¥${(item.price * item.quantity).toFixed(2)}` 
                                  }
                                ]}
                                rowKey="name"
                                pagination={false}
                                size="small"
                              />
                            </div>
                          ),
                          rowExpandable: record => record.items !== undefined && record.items.length > 0,
                        }}
                        columns={[
                          {
                            title: '订单信息',
                            key: 'orderInfo',
                            render: (_, record) => (
                              <div>
                                <div><Text strong>{record.orderNo}</Text></div>
                                <div><Text type="secondary">{record.orderTime}</Text></div>
                                <div style={{ marginTop: 4 }}>{getOrderStatusTag(record.status)}</div>
                              </div>
                            )
                          },
                          {
                            title: '消费类型',
                            key: 'businessType',
                            render: (_, record) => (
                              <Tag icon={getBusinessTypeIcon(record.businessType)} color="blue">
                                {record.businessType}
                              </Tag>
                            ),
                          },
                          {
                            title: '消费金额',
                            dataIndex: 'amount',
                            key: 'amount',
                            render: amount => <Text strong>{`¥${amount.toFixed(2)}`}</Text>,
                            sorter: (a, b) => a.amount - b.amount,
                          },
                          {
                            title: '支付方式',
                            dataIndex: 'paymentMethod',
                            key: 'paymentMethod',
                          },
                          {
                            title: '门店名称',
                            dataIndex: 'storeName',
                            key: 'storeName',
                          },
                          {
                            title: '订单来源',
                            dataIndex: 'orderSource',
                            key: 'orderSource',
                            render: source => <Tag color="cyan">{source}</Tag>
                          },
                          {
                            title: '会员卡级别',
                            dataIndex: 'memberLevel',
                            key: 'memberLevel',
                            render: level => <Tag color={getLevelColor(level)}>{level}</Tag>
                          },
                          {
                            title: '服务管家',
                            dataIndex: 'butlerName',
                            key: 'butlerName',
                            render: butlerName => butlerName ? 
                              <Tag color="purple" icon={<TeamOutlined />}>{butlerName}</Tag> : 
                              <Text type="secondary">-</Text>
                          },
                          {
                            title: '获得积分',
                            dataIndex: 'pointsEarned',
                            key: 'pointsEarned',
                          }
                        ]}
                      />
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Title level={5}>暂无消费记录</Title>
                      <Paragraph type="secondary">该会员尚未有消费记录</Paragraph>
                    </div>
                  )}
                </Card>
              </TabPane>

              <TabPane tab="互动记录" key="4">
                <Card
                  tabList={[
                    { key: 'all', tab: '全部记录' },
                    { key: 'call', tab: '电话沟通' },
                    { key: 'visit', tab: '上门拜访' },
                    { key: 'complaint', tab: '投诉处理' },
                    { key: 'preference', tab: '偏好记录' }
                  ]}
                  activeTabKey={interactionActiveKey}
                  onTabChange={setInteractionActiveKey}
                  extra={
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => message.info('添加互动记录功能开发中')}
                    >
                      添加记录
                    </Button>
                  }
                >
                  {getInteractionRecords(selectedCustomer.key).length > 0 ? (
                    <Timeline style={{ marginTop: 20, marginLeft: 10 }}>
                      {getInteractionRecords(selectedCustomer.key)
                        .filter(record => {
                          if (interactionActiveKey === 'all') return true;
                          return record.interactionType === interactionActiveKey;
                        })
                        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                        .map(record => {
                          const typeInfo = getInteractionTypeDisplay(record.interactionType);
                          return (
                            <Timeline.Item 
                              key={record.id} 
                              color={typeInfo.color}
                              dot={<Avatar size="small" style={{ backgroundColor: typeInfo.color }}>{typeInfo.icon}</Avatar>}
                            >
                              <Card 
                                size="small"
                                title={
                                  <Space>
                                    <Tag color={typeInfo.color} icon={typeInfo.icon}>{typeInfo.text}</Tag>
                                    <Text type="secondary">{record.time}</Text>
                                  </Space>
                                }
                                extra={renderSatisfactionStars(record.satisfaction)}
                                style={{ marginBottom: 16 }}
                              >
                                <div style={{ marginBottom: 8 }}>
                                  <Text strong>处理人: </Text>
                                  <Tag color="blue">{record.staffName}</Tag>
                                  <Text type="secondary" style={{ marginLeft: 8 }}>{record.department}</Text>
                                </div>
                                <Paragraph>{record.content}</Paragraph>
                                {record.followUpAction && (
                                  <div style={{ background: '#f9f9f9', padding: 8, borderRadius: 4, marginTop: 8 }}>
                                    <Text strong>跟进措施: </Text>
                                    <Text>{record.followUpAction}</Text>
                                  </div>
                                )}
                                <div style={{ marginTop: 8, textAlign: 'right' }}>
                                  <Tag 
                                    color={
                                      record.status === 'completed' ? 'success' : 
                                      record.status === 'in-progress' ? 'processing' : 'warning'
                                    }
                                  >
                                    {
                                      record.status === 'completed' ? '已完成' : 
                                      record.status === 'in-progress' ? '处理中' : '待处理'
                                    }
                                  </Tag>
                                </div>
                              </Card>
                            </Timeline.Item>
                          );
                        })}
                    </Timeline>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Title level={5}>暂无互动记录</Title>
                      <Paragraph type="secondary">该会员尚未有互动记录</Paragraph>
                    </div>
                  )}
                </Card>
              </TabPane>
            </Tabs>
          </>
        )}
      </Modal>

      {/* 绑定管家弹窗 */}
      <Modal
        title={`绑定管家 - ${selectedCustomer?.name}`}
        open={bindingModalVisible}
        onCancel={() => setBindingModalVisible(false)}
        onOk={handleBindSubmit}
        okText="确认绑定"
      >
        <Form form={bindingForm} layout="vertical">
          <Form.Item 
            name="butlerId" 
            label="选择管家"
            rules={[{ required: true, message: '请选择管家' }]}
          >
            <Select placeholder="请选择管家">
              {butlerData.filter(butler => butler.status === 'active').map(butler => (
                <Option key={butler.id} value={butler.id}>
                  {butler.name} ({butler.position} - 当前服务{butler.customerCount}位客户)
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item 
            name="bindMethod" 
            label="绑定方式"
            rules={[{ required: true, message: '请选择绑定方式' }]}
          >
            <Select placeholder="请选择绑定方式">
              <Option value="前台引导">前台引导</Option>
              <Option value="预订自动绑定">预订自动绑定</Option>
              <Option value="客户主动要求">客户主动要求</Option>
              <Option value="营销活动">营销活动</Option>
              <Option value="其他方式">其他方式</Option>
            </Select>
          </Form.Item>
          <Form.Item 
            name="notes" 
            label="备注"
          >
            <Input.TextArea rows={4} placeholder="请输入绑定备注信息（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 解绑管家弹窗 */}
      <Modal
        title={`解绑管家 - ${selectedCustomer?.name}`}
        open={unbindModalVisible}
        onCancel={() => setUnbindModalVisible(false)}
        onOk={handleUnbindSubmit}
        okText="确认解绑"
        okButtonProps={{ danger: true }}
      >
        <Form form={unbindForm} layout="vertical">
          <Form.Item label="当前管家">
            <Input value={selectedCustomer?.currentButler?.name} disabled />
          </Form.Item>
          <Form.Item label="绑定时间">
            <Input value={selectedCustomer?.currentButler?.bindTime} disabled />
          </Form.Item>
          <Form.Item 
            name="unbindReason" 
            label="解绑原因"
            rules={[{ required: true, message: '请选择解绑原因' }]}
          >
            <Select placeholder="请选择解绑原因">
              <Option value="客户要求更换">客户要求更换</Option>
              <Option value="管家离职">管家离职</Option>
              <Option value="服务不满意">服务不满意</Option>
              <Option value="系统调整">系统调整</Option>
              <Option value="其他原因">其他原因</Option>
            </Select>
          </Form.Item>
          <Form.Item 
            name="remarks" 
            label="备注说明"
          >
            <Input.TextArea rows={4} placeholder="请输入详细解绑原因（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerManagement; 